import React from 'react';
import Layout from '../../../components/feature/Layout';
import { useSEO, generateWebPageSchema } from '../../../utils/seo';

// FAQ データ
const faqs = [
  {
    question: '無料プリントは本当に無料で使えますか？商用利用は可能ですか？',
    answer: 'ほとんどのサイトは個人利用・教育目的であれば無料で使えます。ただし、学習塾や有料の教室で使う場合は、各サイトの利用規約を確認してください。多くのサイトでは「非営利目的の教育利用」であれば問題ありませんが、商用利用を禁止している場合もあります。',
  },
  {
    question: '中学1年生から使える基礎的なプリントはありますか？',
    answer: 'はい、あります。「ちびむすドリル」や「無料で使える中学学習プリント」は、中学1年生の基礎レベルから段階的に学習できる構成になっています。特に、数学や英語は学年別に分かれているので、お子さんの学年に合わせて選べます。',
  },
  {
    question: '高校受験対策に特化したプリントはどれがおすすめですか？',
    answer: '「みんぷり」と「Study-X」が特におすすめです。どちらも高校入試レベルの問題が豊富で、実戦的な演習ができます。また、「無料で使える中学学習プリント」も、入試対策問題が充実しており、段階的に実力を伸ばせます。',
  },
  {
    question: 'プリントを印刷する際の注意点はありますか？',
    answer: 'PDFファイルをダウンロードする際は、必ず「実際のサイズ」で印刷してください。「ページに合わせる」設定にすると、図形問題や方眼紙の目盛りがずれることがあります。また、両面印刷する場合は、解答ページと問題ページが混ざらないよう注意しましょう。',
  },
  {
    question: '定期テスト対策と高校受験対策、どちらを優先すべきですか？',
    answer: '中学1・2年生は定期テスト対策を優先しましょう。内申点は高校受験に直結するため、まずは学校の授業内容をしっかり理解することが大切です。中学3年生になったら、定期テスト対策と並行して、高校受験対策用のプリントを進めると効果的です。',
  },
  {
    question: '子どもが一人でプリント学習を続けられるか心配です',
    answer: '最初は保護者の方が一緒に学習計画を立て、「今週はこの単元のプリントを3枚やろう」といった具体的な目標を設定すると良いでしょう。また、解き終わったプリントをファイリングして「見える化」することで、達成感が得られ、継続しやすくなります。',
  },
];

export default function JhsFreePrintPage() {
  // SEO設定
  useSEO({
    title: '中学生向け無料プリント教材まとめ｜キョウコレ',
    description: '中学生向けの無料でダウンロードできるプリント教材を教科別にまとめました。学習支援や家庭学習に活用できる教材サイトをご紹介します。',
    keywords: '中学生,無料プリント,教材,ダウンロード,学習支援,家庭学習',
    canonical: (import.meta.env.VITE_SITE_URL || 'https://example.com') + '/features/jhs-free-print',
    jsonLd: generateWebPageSchema(
      '中学生向け無料プリント教材まとめ｜キョウコレ',
      '中学生向けの無料でダウンロードできるプリント教材を教科別にまとめました。学習支援や家庭学習に活用できる教材サイトをご紹介します。',
      '/features/jhs-free-print'
    ),
  });

  const examSites = [
    {
      name: '無料で使える中学学習プリント',
      url: 'https://chugaku.manabihiroba.net/',
      subjects: '数学、国語、理科、社会、英語',
      target: '基礎から入試レベルまで幅広く対応',
      point: '高校入試対策問題が充実しており、段階的に実力を伸ばせる構成が魅力です。',
    },
    {
      name: 'みんぷり',
      url: 'https://min-pri.com/',
      subjects: '英語、数学、国語',
      target: '定期テスト対策と受験対策の両立',
      point: '高校受験に特化したプリントが豊富で、自由に使える点が保護者・先生に好評です。',
    },
    {
      name: 'Study-X 無料学習プリント',
      url: 'https://www.study-x.com/',
      subjects: '英語、数学、国語',
      target: '長文読解力を鍛えたい受験生',
      point: '英語長文問題が50問以上あり、繰り返し解くことで読解力が確実に向上します。',
    },
    {
      name: '中学校数学学習サイト',
      url: 'https://math.005net.com/mondai.php',
      subjects: '数学',
      target: '基本から難関校レベルまで',
      point: '中学数学の基礎から高校受験の難問まで、レベル別に問題が用意されています。',
    },
    {
      name: 'すたぺんドリル【中学生】',
      url: 'https://startoo.co/juniorhighschool/',
      subjects: '英語、数学、国語、理科、社会',
      target: '定期テストと受験対策の両方',
      point: '5教科すべてに対応しており、定期テストから高校受験まで幅広く活用できます。',
    },
  ];

  const mathSites = [
    {
      name: '無料で使える中学学習プリント',
      url: 'https://chugaku.manabihiroba.net/',
      usage: '計算練習から関数・図形まで体系的に学習',
      level: '基礎〜入試レベル',
    },
    {
      name: 'みんぷり 数学プリント',
      url: 'https://min-pri.com/math-pri/',
      usage: '高校受験対策に特化した問題演習',
      level: '標準〜入試レベル',
    },
    {
      name: 'ちびむすドリル 中学数学',
      url: 'https://happylilac.net/jhs-math1.html',
      usage: '学年別の基礎固めに最適',
      level: '基礎〜標準',
    },
    {
      name: '中学校数学学習サイト',
      url: 'https://math.005net.com/mondai.php',
      usage: '難関校向けの応用問題も充実',
      level: '基礎〜難関校レベル',
    },
    {
      name: '学びの森',
      url: 'https://math-free.work/',
      usage: '定期テスト・実力テストの予想問題',
      level: '標準〜入試レベル',
    },
    {
      name: 'ポテスクプリント',
      url: 'https://potablog.com/',
      usage: 'QRコードで答え合わせができる便利な教材',
      level: '基礎〜標準',
    },
    {
      name: '平均ドリル',
      url: 'https://heikin.space/',
      usage: '無料授業動画とプリントで効率的に学習',
      level: '基礎〜標準',
    },
    {
      name: 'hakken.無料学習教材',
      url: 'https://www.hakken.biz/download.php',
      usage: '学習塾で実際に使われている実践的な教材',
      level: '基礎〜標準',
    },
  ];

  const englishSites = [
    {
      name: 'みんぷり 英語プリント',
      url: 'https://min-pri.com/english-pri/',
      usage: '高校受験対策の文法・長文読解',
      level: '標準〜入試レベル',
    },
    {
      name: 'Study-X 無料学習プリント',
      url: 'https://www.study-x.com/',
      usage: '長文読解問題50問以上で読解力強化',
      level: '標準〜入試レベル',
    },
    {
      name: 'ちびむすドリル【中学生】',
      url: 'https://happylilac.net/highschool.html',
      usage: '基礎文法から応用まで幅広くカバー',
      level: '基礎〜標準',
    },
    {
      name: 'ポテスクプリント',
      url: 'https://potablog.com/',
      usage: 'QRコードで発音確認もできる英語教材',
      level: '基礎〜標準',
    },
    {
      name: 'ぷりんと保管庫',
      url: 'https://desafiante.net/',
      usage: '英語の基礎固めに最適なプリント集',
      level: '基礎〜標準',
    },
    {
      name: 'hakken.無料学習教材',
      url: 'https://www.hakken.biz/download.php',
      usage: 'わかりやすい解説付きの英語教材',
      level: '基礎〜標準',
    },
  ];

  const japaneseSites = [
    {
      name: 'みんぷり 国語プリント',
      url: 'https://min-pri.com/japanese-pri/',
      usage: '高校受験対策の読解・古文・漢文',
      level: '標準〜入試レベル',
    },
    {
      name: 'ちびむすドリル 中学漢字',
      url: 'https://happylilac.net/sy-kanzi-zyoyo.html',
      usage: '中学・高校で習う漢字の練習とテスト',
      level: '基礎〜標準',
    },
    {
      name: '無料で使える中学学習プリント',
      url: 'https://chugaku.manabihiroba.net/',
      usage: '漢字・文法・読解を総合的に学習',
      level: '基礎〜入試レベル',
    },
    {
      name: 'ぷりんと保管庫',
      url: 'https://desafiante.net/',
      usage: '国語の基礎力を固める問題集',
      level: '基礎〜標準',
    },
  ];

  const scienceSites = [
    {
      name: '無料で使える中学学習プリント',
      url: 'https://chugaku.manabihiroba.net/',
      usage: '物理・化学・生物・地学を体系的に学習',
      level: '基礎〜入試レベル',
    },
    {
      name: 'ちびむすドリル 中学理科',
      url: 'https://happylilac.net/jhs-science1.html',
      usage: '要点まとめと練習問題で効率的に復習',
      level: '基礎〜標準',
    },
    {
      name: 'ポテスクプリント',
      url: 'https://potablog.com/',
      usage: 'QRコードで答え合わせができる理科教材',
      level: '基礎〜標準',
    },
    {
      name: '平均ドリル',
      url: 'https://heikin.space/',
      usage: '無料授業動画とプリントで理解を深める',
      level: '基礎〜標準',
    },
  ];

  const socialSites = [
    {
      name: 'ちびむすドリル【中学生】',
      url: 'https://happylilac.net/highschool.html',
      usage: '地理・歴史・公民を幅広くカバー',
      level: '基礎〜標準',
    },
    {
      name: 'ぷりんと保管庫',
      url: 'https://desafiante.net/',
      usage: '社会科の基礎固めに最適',
      level: '基礎〜標準',
    },
    {
      name: '平均ドリル',
      url: 'https://heikin.space/',
      usage: '無料授業動画とプリントで効率学習',
      level: '基礎〜標準',
    },
    {
      name: 'おかわりドリル 中学生',
      url: 'https://www.okadori.net/category/tyuugaku/',
      usage: '社会科の重要ポイントを押さえた問題集',
      level: '基礎〜標準',
    },
  ];

  const pastExamSites = [
    {
      name: 'リセマム 都道府県別公立高校入試',
      url: 'https://s.resemom.jp/pages/public-highschool-exam/index.html',
      description: '47都道府県すべての公立高校入試問題と正答を掲載。各都道府県の教育委員会が提供する最新情報に基づいています。',
    },
    {
      name: 'JS88.com 私立高校入試過去問',
      url: 'https://school.js88.com/scl_h/kakomon',
      description: '全国770校以上の私立高校の入試過去問題を無料で公開。有名進学私立高校の傾向と対策に活用できます。',
    },
    {
      name: 'ちばのやる気学習ガイド',
      url: 'https://www.pref.chiba.lg.jp/kyouiku/shidou/gakuryoku/yaruki.html',
      description: '千葉県教育委員会が提供する思考力・判断力・表現力を鍛える良問集。5教科すべてに対応しています。',
    },
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 py-12">
        <div className="max-w-5xl mx-auto px-4">
          {/* ヘッダー */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              中学無料プリント＆高校受験対策プリント特集
            </h1>
            <p className="text-lg text-gray-600">
              教科別・目的別におすすめの無料学習プリントサイトをまとめました
            </p>
          </div>

          {/* リード文 */}
          <div className="bg-white rounded-xl shadow-sm p-8 mb-12">
            <p className="text-gray-700 leading-relaxed mb-4">
              中学生になると、学習内容が一気に難しくなり、プリントや問題集の量も増えていきます。市販の問題集をすべて揃えようとすると、5教科分で数万円かかることも珍しくありません。しかし、インターネット上には、学校の先生や教育関係者が作成した高品質な無料プリントが数多く公開されています。
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              この特集ページでは、中学生の保護者の方や、学校・学習塾・子どもの居場所で指導されている先生方に向けて、「中学　無料　プリント」で検索してたどり着いた方が、すぐに使える無料プリントサイトを教科別・目的別に整理してご紹介します。
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>保護者の方へ：</strong>定期テスト対策から高校受験対策まで、お子さんの学習レベルに合わせたプリントを選べます。印刷して渡すだけで、自宅学習の教材として活用できます。
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>先生・スタッフの方へ：</strong>授業の補助教材や宿題、テスト対策プリントとして、すぐに使える素材が揃っています。特に、高校受験対策に力を入れたい場合に役立つサイトを厳選しました。
            </p>
          </div>

          {/* セクション1：高校受験対策に強いサイト */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-teal-500 pb-2">
              高校受験対策にまず押さえたい中学無料プリントサイト
            </h2>
            <p className="text-gray-700 mb-6">
              高校入試に向けて実力をつけたい中学生に特におすすめのサイトをピックアップしました。基礎固めから入試レベルの応用問題まで、段階的に学習できる教材が揃っています。
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {examSites.map((site, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow border border-slate-100 p-6"
                >
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {site.name}
                  </h3>
                  <div className="space-y-2 mb-4">
                    <p className="text-sm text-gray-600">
                      <span className="font-semibold">掲載教科：</span>
                      {site.subjects}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-semibold">向いている受験生：</span>
                      {site.target}
                    </p>
                  </div>
                  <p className="text-sm text-teal-700 bg-teal-50 p-3 rounded-lg mb-4">
                    <i className="ri-lightbulb-line mr-1"></i>
                    {site.point}
                  </p>
                  <a
                    href={site.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full px-6 py-3 bg-teal-500 text-white text-center rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    サイトへ（新しいタブで開く）
                    <i className="ri-external-link-line ml-2"></i>
                  </a>
                </div>
              ))}
            </div>
          </section>

          {/* セクション2：教科別サイト一覧 */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-teal-500 pb-2">
              教科別に探せる中学無料プリントサイト
            </h2>
            <p className="text-gray-700 mb-6">
              英語・数学・国語・理科・社会の5教科ごとに、おすすめの無料プリントサイトを整理しました。各教科で特に使いやすいサイトを厳選しています。
            </p>

            {/* 数学 */}
            <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 text-white rounded-full flex items-center justify-center mr-3">
                  <i className="ri-calculator-line"></i>
                </span>
                数学
              </h3>
              <div className="space-y-4">
                {mathSites.map((site, index) => (
                  <div
                    key={index}
                    className="border-l-4 border-teal-500 pl-4 py-2"
                  >
                    <a
                      href={site.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-lg font-semibold text-teal-600 hover:text-teal-700 hover:underline cursor-pointer"
                    >
                      {site.name}
                      <i className="ri-external-link-line ml-1 text-sm"></i>
                    </a>
                    <p className="text-sm text-gray-600 mt-1">
                      <span className="font-semibold">おすすめの使い方：</span>
                      {site.usage}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      レベル：{site.level}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* 英語 */}
            <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 text-white rounded-full flex items-center justify-center mr-3">
                  <i className="ri-global-line"></i>
                </span>
                英語
              </h3>
              <div className="space-y-4">
                {englishSites.map((site, index) => (
                  <div
                    key={index}
                    className="border-l-4 border-teal-500 pl-4 py-2"
                  >
                    <a
                      href={site.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-lg font-semibold text-teal-600 hover:text-teal-700 hover:underline cursor-pointer"
                    >
                      {site.name}
                      <i className="ri-external-link-line ml-1 text-sm"></i>
                    </a>
                    <p className="text-sm text-gray-600 mt-1">
                      <span className="font-semibold">おすすめの使い方：</span>
                      {site.usage}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      レベル：{site.level}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* 国語 */}
            <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 text-white rounded-full flex items-center justify-center mr-3">
                  <i className="ri-book-open-line"></i>
                </span>
                国語
              </h3>
              <div className="space-y-4">
                {japaneseSites.map((site, index) => (
                  <div
                    key={index}
                    className="border-l-4 border-teal-500 pl-4 py-2"
                  >
                    <a
                      href={site.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-lg font-semibold text-teal-600 hover:text-teal-700 hover:underline cursor-pointer"
                    >
                      {site.name}
                      <i className="ri-external-link-line ml-1 text-sm"></i>
                    </a>
                    <p className="text-sm text-gray-600 mt-1">
                      <span className="font-semibold">おすすめの使い方：</span>
                      {site.usage}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      レベル：{site.level}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* 理科 */}
            <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 text-white rounded-full flex items-center justify-center mr-3">
                  <i className="ri-flask-line"></i>
                </span>
                理科
              </h3>
              <div className="space-y-4">
                {scienceSites.map((site, index) => (
                  <div
                    key={index}
                    className="border-l-4 border-teal-500 pl-4 py-2"
                  >
                    <a
                      href={site.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-lg font-semibold text-teal-600 hover:text-teal-700 hover:underline cursor-pointer"
                    >
                      {site.name}
                      <i className="ri-external-link-line ml-1 text-sm"></i>
                    </a>
                    <p className="text-sm text-gray-600 mt-1">
                      <span className="font-semibold">おすすめの使い方：</span>
                      {site.usage}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      レベル：{site.level}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* 社会 */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 text-white rounded-full flex items-center justify-center mr-3">
                  <i className="ri-earth-line"></i>
                </span>
                社会
              </h3>
              <div className="space-y-4">
                {socialSites.map((site, index) => (
                  <div
                    key={index}
                    className="border-l-4 border-teal-500 pl-4 py-2"
                  >
                    <a
                      href={site.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-lg font-semibold text-teal-600 hover:text-teal-700 hover:underline cursor-pointer"
                    >
                      {site.name}
                      <i className="ri-external-link-line ml-1 text-sm"></i>
                    </a>
                    <p className="text-sm text-gray-600 mt-1">
                      <span className="font-semibold">おすすめの使い方：</span>
                      {site.usage}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      レベル：{site.level}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* セクション3：入試過去問リンク集 */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-teal-500 pb-2">
              公立・私立高校の入試過去問も無料でチェック
            </h2>
            <p className="text-gray-700 mb-6">
              高校受験対策には、実際の入試問題を解くことが欠かせません。全国の公立高校・私立高校の過去問を無料で公開しているサイトをまとめました。
            </p>
            <div className="space-y-6">
              {pastExamSites.map((site, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-orange-500"
                >
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    <a
                      href={site.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-teal-600 hover:text-teal-700 hover:underline cursor-pointer"
                    >
                      {site.name}
                      <i className="ri-external-link-line ml-2"></i>
                    </a>
                  </h3>
                  <p className="text-gray-700">{site.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* セクション4：効果的な使い方ガイド */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-teal-500 pb-2">
              中学無料プリントを"やりっぱなし"にしないコツ
            </h2>
            <div className="bg-white rounded-xl shadow-sm p-8">
              <p className="text-gray-700 leading-relaxed mb-4">
                無料プリントは手軽に使える反面、「印刷して解いて終わり」になりがちです。しかし、学習効果を最大化するには、<strong>「解き直し」と「段階的な学習」</strong>が欠かせません。ここでは、中学生の無料プリント学習を効果的に進めるためのポイントをご紹介します。
              </p>

              <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6">
                1. 単元ごとに「基本→標準→入試レベル」と段階を踏む
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                いきなり高校受験レベルの問題に挑戦するのではなく、まずは基本問題で土台を固めましょう。例えば、数学の「関数」なら、「基本的な式の計算」→「グラフの読み取り」→「入試の応用問題」という順番で進めると、無理なく実力がつきます。
              </p>

              <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6">
                2. 間違えた問題にチェックをつけて「解き直しプリント」を作る
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                一度解いたプリントは、間違えた問題に印をつけて保管しておきましょう。定期テストや高校受験の直前に、その「解き直しプリント」だけを集中的に復習すると、効率的に弱点を克服できます。保護者の方は、お子さんが間違えた問題をファイリングする手伝いをしてあげると良いでしょう。
              </p>

              <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6">
                3. 定期テスト対策と高校受験対策で使い分ける
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                中学1・2年生は、まず定期テスト対策を優先しましょう。学校の授業進度に合わせて、該当単元のプリントを解くことで、内申点アップにつながります。中学3年生になったら、高校受験対策用のプリントを並行して進め、入試本番に向けた実戦力を養いましょう。
              </p>

              <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6">
                4. 保護者・先生が声かけしやすい簡単なフレーズ例
              </h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>「今日はどの単元のプリントをやった？」</li>
                <li>「間違えた問題、もう一度解いてみようか」</li>
                <li>「この問題、前より解けるようになったね！」</li>
                <li>「次のテストまでに、あと何枚プリントをやる予定？」</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                こうした声かけを通じて、お子さんの学習状況を把握し、適切なサポートができます。特に、「成長を認める言葉」は、中学生の学習意欲を高める大きな力になります。
              </p>
            </div>
          </section>

          {/* セクション5：FAQ */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-teal-500 pb-2">
              中学無料プリントに関するよくある質問
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-sm p-6"
                >
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-start">
                    <span className="w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                      Q
                    </span>
                    {faq.question}
                  </h3>
                  <div className="ml-11">
                    <p className="text-gray-700 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* まとめ */}
          <div className="bg-gradient-to-r from-teal-500 to-blue-500 rounded-xl shadow-lg p-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-4">
              中学無料プリントで、お子さんの学習をサポートしましょう
            </h2>
            <p className="text-lg leading-relaxed">
              この特集ページでご紹介した無料プリントサイトを活用すれば、<br />
              定期テスト対策から高校受験対策まで、幅広く対応できます。<br />
              ぜひ、お子さんの学習レベルや目的に合わせて、最適なサイトを選んでください。
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}